import { GetContract } from "@/services/fhir/contract/GetContract";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useOrganizationContext } from "./OrganizationContext";
import { Contract } from "fhir/r5";

type ContractResource = Contract;

/** Identifier FHIR do Contract desta extensão: "prontoAtendimento" */
interface ProntoAtendimentoContractContextValue {
  contractProntoAtendimento: ContractResource | null;
  refetchContract: () => Promise<void>;
  loading: boolean;
}

interface ProntoAtendimentoContractContextProviderProps {
  children: React.ReactNode;
}

export const ProntoAtendimentoContractContext =
  createContext<ProntoAtendimentoContractContextValue>(
    {} as ProntoAtendimentoContractContextValue,
  );

export function ProntoAtendimentoContractContextProvider({
  children,
}: ProntoAtendimentoContractContextProviderProps) {
  const { organization } = useOrganizationContext();
  const [contractProntoAtendimento, setContractProntoAtendimento] =
    useState<ContractResource | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchContract = useCallback(async () => {
    setLoading(true);
    try {
      if (!organization?.id) {
        console.warn("Nenhuma organização selecionada");
        setContractProntoAtendimento(null);
        return;
      }

      // Mesmo padrão TISS (healthPlan): Contract executed por identifier
      const contractData = await GetContract({
        organization: organization.id,
        count: 1,
        sort: true,
        status: "executed",
        identifier: "prontoAtendimento",
      });

      if (contractData?.entry?.[0]?.resource) {
        const resource = contractData.entry?.[0]?.resource;
        setContractProntoAtendimento(resource);
      } else {
        setContractProntoAtendimento(null);
      }
    } catch (error) {
      console.error("Erro ao buscar contrato de Pronto Atendimento:", error);
      setContractProntoAtendimento(null);
    } finally {
      setLoading(false);
    }
  }, [organization]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const contextValue: ProntoAtendimentoContractContextValue = {
    loading,
    contractProntoAtendimento,
    refetchContract: fetchContract,
  };

  return (
    <ProntoAtendimentoContractContext.Provider value={contextValue}>
      {children}
    </ProntoAtendimentoContractContext.Provider>
  );
}

export function useProntoAtendimentoContractContext() {
  return useContext(ProntoAtendimentoContractContext);
}
